// src/components/auth/HasRole.jsx
import React from 'react';
import { hasRole, hasAnyRole, isAdmin, isDirector, isManager, isFieldWorker } from './authUtils';

/**
 * HasRole Component
 * 
 * Wrapper component that conditionally renders children based on user roles.
 * Supports single role, any of multiple roles, and specific role type checks.
 * Can also render fallback content when role check fails.
 * 
 * @param {string} role - Single role code
 * @param {string[]} anyRoles - Array of roles (user needs ANY)
 * @param {string[]} allRoles - Array of roles (user needs ALL - typically just one)
 * @param {boolean} isAdmin - Check if user is Administrator
 * @param {boolean} isDirector - Check if user is any Director role
 * @param {boolean} isManager - Check if user is any Manager role
 * @param {boolean} isFieldWorker - Check if user is Supervisor or Mandor
 * @param {React.ReactNode} children - Content to render if role check passes
 * @param {React.ReactNode} fallback - Content to render if role check fails (optional)
 * @param {boolean} hide - If true, renders nothing when denied (default: true)
 * 
 * @example
 * // Single role check
 * <HasRole role={USER_ROLES.ADMINISTRATOR}>
 *   <AdminPanel />
 * </HasRole>
 * 
 * @example
 * // Any of multiple roles
 * <HasRole anyRoles={[USER_ROLES.SALES_MANAGER, USER_ROLES.SALES]}>
 *   <CustomerSection />
 * </HasRole>
 * 
 * @example
 * // Role type check
 * <HasRole isManager={true}>
 *   <ManagerDashboard />
 * </HasRole>
 * 
 * @example
 * // With fallback
 * <HasRole 
 *   role={USER_ROLES.ADMINISTRATOR}
 *   fallback={<div>Admin access required</div>}
 *   hide={false}
 * >
 *   <SystemSettings />
 * </HasRole>
 */
const HasRole = ({
  role,
  anyRoles,
  allRoles,
  isAdmin: checkIsAdmin = false,
  isDirector: checkIsDirector = false,
  isManager: checkIsManager = false,
  isFieldWorker: checkIsFieldWorker = false,
  children,
  fallback = null,
  hide = true,
}) => {
  // Check roles
  let hasAccess = false;

  if (role) {
    // Single role check
    hasAccess = hasRole(role);
  } else if (anyRoles && anyRoles.length > 0) {
    // Any of multiple roles
    hasAccess = hasAnyRole(anyRoles);
  } else if (allRoles && allRoles.length > 0) {
    // All of multiple roles (typically just one role needed)
    hasAccess = allRoles.every(r => hasRole(r));
  } else if (checkIsAdmin) {
    // Check if Administrator
    hasAccess = isAdmin();
  } else if (checkIsDirector) {
    // Check if any Director role
    hasAccess = isDirector();
  } else if (checkIsManager) {
    // Check if any Manager role
    hasAccess = isManager();
  } else if (checkIsFieldWorker) {
    // Check if field worker (Supervisor or Mandor)
    hasAccess = isFieldWorker();
  } else {
    // No role specified - grant access by default
    hasAccess = true;
  }

  // Render based on access
  if (hasAccess) {
    return <>{children}</>;
  }

  // No access - render fallback or nothing
  if (hide) {
    return null;
  }

  return <>{fallback}</>;
};

/**
 * HasRoleOr Component
 * 
 * Convenience wrapper for checking ANY of multiple roles.
 * Alias for HasRole with anyRoles prop.
 * 
 * @example
 * <HasRoleOr roles={[USER_ROLES.SALES_MANAGER, USER_ROLES.SALES]}>
 *   <SalesSection />
 * </HasRoleOr>
 */
export const HasRoleOr = ({ roles, children, fallback, hide = true }) => {
  return (
    <HasRole anyRoles={roles} fallback={fallback} hide={hide}>
      {children}
    </HasRole>
  );
};

/**
 * IsAdmin Component
 * 
 * Convenience wrapper for Administrator role check.
 * Shortcut for <HasRole isAdmin={true}>
 * 
 * @example
 * <IsAdmin>
 *   <AdminPanel />
 * </IsAdmin>
 */
export const IsAdmin = ({ children, fallback, hide = true }) => {
  return (
    <HasRole isAdmin={true} fallback={fallback} hide={hide}>
      {children}
    </HasRole>
  );
};

/**
 * IsDirector Component
 * 
 * Convenience wrapper for any Director role check.
 * Shortcut for <HasRole isDirector={true}>
 * 
 * @example
 * <IsDirector>
 *   <ExecutiveDashboard />
 * </IsDirector>
 */
export const IsDirector = ({ children, fallback, hide = true }) => {
  return (
    <HasRole isDirector={true} fallback={fallback} hide={hide}>
      {children}
    </HasRole>
  );
};

/**
 * IsManager Component
 * 
 * Convenience wrapper for any Manager role check.
 * Shortcut for <HasRole isManager={true}>
 * 
 * @example
 * <IsManager>
 *   <ManagerDashboard />
 * </IsManager>
 */
export const IsManager = ({ children, fallback, hide = true }) => {
  return (
    <HasRole isManager={true} fallback={fallback} hide={hide}>
      {children}
    </HasRole>
  );
};

/**
 * IsFieldWorker Component
 * 
 * Convenience wrapper for field worker role check (Supervisor or Mandor).
 * Shortcut for <HasRole isFieldWorker={true}>
 * 
 * @example
 * <IsFieldWorker>
 *   <MobileAppDownload />
 * </IsFieldWorker>
 */
export const IsFieldWorker = ({ children, fallback, hide = true }) => {
  return (
    <HasRole isFieldWorker={true} fallback={fallback} hide={hide}>
      {children}
    </HasRole>
  );
};

/**
 * IsNotFieldWorker Component
 * 
 * Inverse check - shows content if user is NOT a field worker.
 * Useful for web-only features.
 * 
 * @example
 * <IsNotFieldWorker>
 *   <DesktopFeatures />
 * </IsNotFieldWorker>
 */
export const IsNotFieldWorker = ({ children, fallback, hide = true }) => {
  const hasAccess = !isFieldWorker();

  if (hasAccess) {
    return <>{children}</>;
  }

  if (hide) {
    return null;
  }

  return <>{fallback}</>;
};

/**
 * WithRole HOC (Higher Order Component)
 * 
 * HOC wrapper for role checking.
 * Useful for wrapping entire components with role checks.
 * 
 * @param {React.Component} Component - Component to wrap
 * @param {string|string[]} roles - Required role(s)
 * @param {React.Component} FallbackComponent - Component to render when denied (optional)
 * 
 * @example
 * const AdminPage = () => <div>Admin Content</div>;
 * const ProtectedAdminPage = WithRole(AdminPage, USER_ROLES.ADMINISTRATOR);
 * 
 * // Usage
 * <ProtectedAdminPage />
 * 
 * @example
 * // Multiple roles
 * const SalesPage = () => <div>Sales Content</div>;
 * const ProtectedSalesPage = WithRole(
 *   SalesPage, 
 *   [USER_ROLES.SALES_MANAGER, USER_ROLES.SALES]
 * );
 * 
 * @example
 * // With fallback
 * const NoAccess = () => <div>Access denied</div>;
 * const ProtectedPage = WithRole(
 *   SecretPage,
 *   USER_ROLES.ADMINISTRATOR,
 *   NoAccess
 * );
 */
export const WithRole = (Component, roles, FallbackComponent = null) => {
  return (props) => {
    let hasAccess = false;

    if (Array.isArray(roles)) {
      hasAccess = hasAnyRole(roles);
    } else {
      hasAccess = hasRole(roles);
    }

    if (hasAccess) {
      return <Component {...props} />;
    }

    if (FallbackComponent) {
      return <FallbackComponent />;
    }

    return null;
  };
};

/**
 * RoleBadge Component
 * 
 * Display user's role as a badge (bonus utility component).
 * Useful for showing role in UI.
 * 
 * @param {string} role - Role to display (optional, defaults to current user's role)
 * @param {string} className - Additional CSS classes
 * 
 * @example
 * <RoleBadge />
 * 
 * @example
 * <RoleBadge role={USER_ROLES.ADMINISTRATOR} />
 */
export const RoleBadge = ({ role, className = '' }) => {
  const { getUserRole } = require('./authUtils');
  const { ROLE_LABELS } = require('./constants');
  
  const userRole = role || getUserRole();
  const roleLabel = ROLE_LABELS[userRole] || userRole;

  if (!userRole) {
    return null;
  }

  // Role-based colors
  const roleColors = {
    ADMINISTRATOR: 'bg-purple-100 text-purple-800',
    DIRECTOR: 'bg-indigo-100 text-indigo-800',
    FINANCE_DIRECTOR: 'bg-blue-100 text-blue-800',
    SALES_MANAGER: 'bg-green-100 text-green-800',
    PROCUREMENT_MANAGER: 'bg-cyan-100 text-cyan-800',
    FINANCE_MANAGER: 'bg-blue-100 text-blue-800',
    PLANNING_ENGINEERING_MANAGER: 'bg-teal-100 text-teal-800',
    SUPERVISOR: 'bg-orange-100 text-orange-800',
    MANDOR: 'bg-yellow-100 text-yellow-800',
    default: 'bg-gray-100 text-gray-800',
  };

  const colorClass = roleColors[userRole] || roleColors.default;

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass} ${className}`}>
      {roleLabel}
    </span>
  );
};

export default HasRole;